import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PERMISSIONS } from '@lms/shared-types';
import { Badge, ConfirmModal, Loader, useToast } from '@lms/ui';
import { SearchInput } from '@/components/SearchInput';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { EnrollmentStatusBadge } from '@/components/courses/EnrollmentStatusBadge';
import { listResource, api } from '@/lib/api';
import { enrollmentSuccessToast, roleActionErrorToast } from '@/lib/user-feedback';
import { usePermissions } from '@/hooks/usePermissions';
import { inscripcionesIndexBack, searchSuffix } from '@/lib/enrollment-navigation';

interface Enrollment {
  id: string;
  user?: { id: string; name: string; email?: string };
  assignableItem?: { title?: string };
  assignableType?: { label?: string } | string;
  enrollmentType?: { name?: string };
  status?: string;
  enrolledAt?: string;
  expiresAt?: string;
  endsAt?: string;
}

interface CourseOption {
  id: string;
  name: string;
}

const FILTER_FIELD =
  'w-full rounded-lg border border-dash-outline-variant bg-white py-2.5 px-4 text-base text-dash-on-surface outline-none transition-all focus:border-dash-secondary focus:ring-2 focus:ring-dash-secondary/20';

const PER_PAGE = 10;

const STATUS_LABELS: Record<string, string> = {
  active: 'Activa',
  pending: 'Pendiente',
  suspended: 'Suspendida',
  completed: 'Completada',
};

function initials(name: string) {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? parts[0]?.[1] ?? '')).toUpperCase();
}

function EnrollmentRowActions({
  enrollmentId,
  studentName,
  returnQuery,
}: {
  enrollmentId: string;
  studentName: string;
  returnQuery: string;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(PERMISSIONS.INSCRIPCIONES_ADMINISTRAR);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const deleteMutation = useMutation({
    mutationFn: () => api(`/enrollments/${enrollmentId}`, { method: 'DELETE' }),
    onSuccess: () => {
      setDeleteOpen(false);
      toast(enrollmentSuccessToast('delete'));
      void queryClient.invalidateQueries({ queryKey: ['enrollments'] });
    },
    onError: (error) => {
      toast(roleActionErrorToast(error, 'No se pudo eliminar la matrícula'));
    },
  });

  return (
    <>
      <div className="flex items-center justify-end gap-1">
        <Link
          to={`/inscripciones/${enrollmentId}${returnQuery}`}
          className="lms-gradebook__action"
          title="Ver"
          aria-label={`Ver matrícula de ${studentName}`}
        >
          <span className="material-symbols-outlined" aria-hidden="true">
            visibility
          </span>
        </Link>
        {canManage && (
          <>
            <Link
              to={`/inscripciones/${enrollmentId}/edit${returnQuery}`}
              className="lms-gradebook__action"
              title="Editar"
              aria-label={`Editar matrícula de ${studentName}`}
            >
              <span className="material-symbols-outlined" aria-hidden="true">
                edit
              </span>
            </Link>
            <button
              type="button"
              className="lms-gradebook__action"
              title="Eliminar"
              aria-label={`Eliminar matrícula de ${studentName}`}
              onClick={() => setDeleteOpen(true)}
            >
              <span className="material-symbols-outlined" aria-hidden="true">
                delete
              </span>
            </button>
          </>
        )}
      </div>

      <ConfirmModal
        open={deleteOpen}
        title="Eliminar matrícula"
        description={`¿Está seguro de eliminar la matrícula de «${studentName}»? Esta acción no se puede deshacer.`}
        confirmLabel="Eliminar"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteMutation.mutate()}
        onClose={() => setDeleteOpen(false)}
      />
    </>
  );
}

export function InscripcionesPage() {
  const [searchParams] = useSearchParams();
  const { hasPermission } = usePermissions();
  const canAssignEnrollment = hasPermission(PERMISSIONS.INSCRIPCIONES_ASIGNAR);
  const initialCourseId = searchParams.get('course_id') ?? '';
  const back = inscripcionesIndexBack(searchParams);
  const suffix = searchSuffix(searchParams);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [courseId, setCourseId] = useState(initialCourseId);
  const [enrollmentType, setEnrollmentType] = useState('');
  const [page, setPage] = useState(1);
  const appliedSearch = useDebouncedValue(search, 300).trim();

  useEffect(() => {
    setCourseId(initialCourseId);
  }, [initialCourseId]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['enrollments', courseId, status, appliedSearch],
    queryFn: () =>
      listResource<Enrollment>('/enrollments', {
        perPage: 100,
        ...(courseId ? { courseId } : {}),
        ...(status ? { status } : {}),
        ...(appliedSearch ? { search: appliedSearch } : {}),
      }),
  });

  const { data: coursesData } = useQuery({
    queryKey: ['courses', 'enrollment-filter'],
    queryFn: () => listResource<CourseOption>('/courses'),
  });

  const enrollments = useMemo(() => {
    const rows = data?.data ?? [];
    if (!enrollmentType) return rows;
    return rows.filter((enrollment) => enrollment.enrollmentType?.name === enrollmentType);
  }, [data?.data, enrollmentType]);

  const filteredTotal = enrollments.length;
  const totalPages = Math.max(1, Math.ceil(filteredTotal / PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const firstItem = filteredTotal === 0 ? 0 : (currentPage - 1) * PER_PAGE + 1;
  const lastItem = Math.min(currentPage * PER_PAGE, filteredTotal);

  const rows = useMemo(() => {
    const start = (currentPage - 1) * PER_PAGE;
    return enrollments.slice(start, start + PER_PAGE);
  }, [enrollments, currentPage]);

  useEffect(() => {
    setPage(1);
  }, [appliedSearch, status, courseId, enrollmentType]);

  const courses = coursesData?.data ?? [];

  function clearFilters() {
    setSearch('');
    setStatus('');
    setCourseId('');
    setEnrollmentType('');
  }

  return (
    <div className="lms-site-admin-hub-page font-sans">
      <div className="mb-4 flex items-start gap-4">
        <HubBackLink to={back.to} label={back.label} />
        <div>
          <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">Matrículas</h1>
          <p className="mt-1 max-w-2xl text-dash-on-surface-variant">Matricular estudiantes</p>
        </div>
      </div>

      <div className="flex flex-col gap-8">
        <section className="lms-credentials-glass-panel rounded-xl border border-dash-outline-variant p-6 shadow-sm md:p-8">
          <form method="get" action="/inscripciones" onSubmit={(event) => event.preventDefault()}>
            <div className="flex flex-col gap-6 lg:flex-row lg:items-end">
            <div className="grid min-w-0 flex-1 grid-cols-1 items-end gap-6 md:grid-cols-2 lg:grid-cols-4">
              <div className="space-y-2">
                <label htmlFor="enrollment-search" className="text-xs font-semibold text-dash-on-surface-variant">
                  Buscar
                </label>
                <SearchInput
                  id="enrollment-search"
                  name="search"
                  value={search}
                  onChange={setSearch}
                  placeholder="Nombre o email..."
                  aria-label="Buscar inscripción"
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="enrollment-status" className="text-xs font-semibold text-dash-on-surface-variant">
                  Estado
                </label>
                <select
                  id="enrollment-status"
                  name="status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className={`${FILTER_FIELD} cursor-pointer appearance-none`}
                >
                  <option value="">Todos</option>
                  {Object.entries(STATUS_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <label htmlFor="enrollment-course" className="text-xs font-semibold text-dash-on-surface-variant">
                  Curso
                </label>
                <select
                  id="enrollment-course"
                  name="course_id"
                  value={courseId}
                  onChange={(e) => setCourseId(e.target.value)}
                  className={`${FILTER_FIELD} cursor-pointer appearance-none`}
                >
                  <option value="">Todos</option>
                  {courses.map((course) => (
                    <option key={course.id} value={course.id}>
                      {course.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <label htmlFor="enrollment-type" className="text-xs font-semibold text-dash-on-surface-variant">
                  Tipo
                </label>
                <select
                  id="enrollment-type"
                  name="enrollment_type"
                  value={enrollmentType}
                  onChange={(e) => setEnrollmentType(e.target.value)}
                  className={`${FILTER_FIELD} cursor-pointer appearance-none`}
                >
                  <option value="">Todos</option>
                </select>
              </div>
            </div>

            <div className="flex shrink-0 gap-3">
              <button
                type="button"
                onClick={clearFilters}
                className="inline-flex items-center justify-center rounded-lg border border-dash-outline-variant px-6 py-2.5 text-sm font-medium text-dash-on-surface-variant transition-colors hover:bg-secondary-100"
              >
                Limpiar
              </button>
            </div>
            </div>
          </form>
        </section>

        {error && (
          <p className="text-sm text-red-600" role="alert">
            {(error as Error).message}
          </p>
        )}

        <div className="lms-gradebook lms-gradebook--forum">
          <div className="lms-course-forum-card">
            {isLoading ? (
              <div className="flex justify-center py-12">
                <Loader />
              </div>
            ) : enrollments.length === 0 ? (
              <div className="lms-gradebook__table-wrap">
                <div className="lms-gradebook__empty">
                  <div className="lms-gradebook__empty-icon" aria-hidden="true">
                    <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>
                      group_off
                    </span>
                  </div>
                  <h3>Sin matrículas</h3>
                  <p>No hay matrículas que coincidan con los filtros.</p>
                </div>
              </div>
            ) : (
              <>
                <div className="lms-gradebook__table-wrap">
                  <table className="lms-gradebook__table">
                    <thead>
                      <tr>
                        <th className="lms-gradebook__th" scope="col">
                          Estudiante
                        </th>
                        <th className="lms-gradebook__th" scope="col">
                          Objeto
                        </th>
                        <th className="lms-gradebook__th" scope="col">
                          Tipo
                        </th>
                        <th className="lms-gradebook__th" scope="col">
                          Estado
                        </th>
                        <th className="lms-gradebook__th" scope="col">
                          Vigencia
                        </th>
                        <th className="lms-gradebook__th" scope="col">
                          Inscrito
                        </th>
                        <th className="lms-gradebook__th lms-gradebook__th--actions" scope="col">
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((enrollment) => {
                        const studentName = enrollment.user?.name ?? '—';
                        const showUrl = `/inscripciones/${enrollment.id}${suffix}`;
                        const typeLabel =
                          typeof enrollment.assignableType === 'string'
                            ? enrollment.assignableType
                            : enrollment.assignableType?.label;
                        const validity = enrollment.expiresAt ?? enrollment.endsAt;

                        return (
                          <tr key={enrollment.id}>
                            <th className="lms-gradebook__td" scope="row">
                              <div className="lms-gradebook__student">
                                <div className="lms-gradebook__avatar" aria-hidden="true">
                                  {initials(studentName)}
                                </div>
                                <div className="min-w-0">
                                  <Link to={showUrl} className="lms-gradebook__name">
                                    {studentName}
                                  </Link>
                                  {enrollment.user?.email ? (
                                    <a href={`mailto:${enrollment.user.email}`} className="lms-gradebook__mail block truncate">
                                      {enrollment.user.email}
                                    </a>
                                  ) : null}
                                </div>
                              </div>
                            </th>
                            <td className="lms-gradebook__td">
                              <div>{enrollment.assignableItem?.title ?? '—'}</div>
                              {typeLabel ? <div className="lms-gradebook__muted">{typeLabel}</div> : null}
                            </td>
                            <td className="lms-gradebook__td">
                              {enrollment.enrollmentType?.name ? (
                                <Badge>{enrollment.enrollmentType.name}</Badge>
                              ) : (
                                <span className="lms-gradebook__muted">—</span>
                              )}
                            </td>
                            <td className="lms-gradebook__td">
                              <EnrollmentStatusBadge status={enrollment.status} />
                            </td>
                            <td className="lms-gradebook__td lms-gradebook__td--nowrap">
                              {validity ? (
                                new Date(validity).toLocaleDateString('es')
                              ) : (
                                <span className="lms-gradebook__muted">Sin vencimiento</span>
                              )}
                            </td>
                            <td className="lms-gradebook__td lms-gradebook__td--nowrap">
                              {enrollment.enrolledAt ? (
                                new Date(enrollment.enrolledAt).toLocaleDateString('es')
                              ) : (
                                <span className="lms-gradebook__muted">—</span>
                              )}
                            </td>
                            <td className="lms-gradebook__td lms-gradebook__td--actions">
                              <EnrollmentRowActions
                                enrollmentId={enrollment.id}
                                studentName={studentName}
                                returnQuery={suffix}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="lms-course-forum-card__footer">
                  <span className="lms-course-forum-card__meta">
                    Mostrando <strong>{firstItem}</strong> a <strong>{lastItem}</strong> de{' '}
                    <strong>{filteredTotal}</strong> resultados
                  </span>
                  <div className="lms-course-forum-card__pager">
                    <button
                      type="button"
                      className="lms-course-forum-card__pager-btn"
                      aria-label="Página anterior"
                      disabled={currentPage <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      <span className="material-symbols-outlined" aria-hidden="true">
                        chevron_left
                      </span>
                    </button>
                    <span className="lms-course-forum-card__pager-current">{currentPage}</span>
                    <button
                      type="button"
                      className="lms-course-forum-card__pager-btn"
                      aria-label="Página siguiente"
                      disabled={currentPage >= totalPages}
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    >
                      <span className="material-symbols-outlined" aria-hidden="true">
                        chevron_right
                      </span>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {canAssignEnrollment && (
        <Link
          to="/inscripciones/bulk"
          className="group fixed bottom-8 right-8 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-dash-primary text-white shadow-lg transition-transform hover:scale-105 active:scale-95"
          aria-label="Matricular estudiante"
        >
          <span className="material-symbols-outlined text-3xl" style={{ fontVariationSettings: "'FILL' 1" }} aria-hidden="true">
            person_add
          </span>
          <span className="pointer-events-none absolute right-full mr-4 whitespace-nowrap rounded-lg bg-dash-on-surface px-3 py-1 text-sm text-white opacity-0 shadow-sm transition-opacity group-hover:opacity-100">
            Matricular estudiante
          </span>
        </Link>
      )}
    </div>
  );
}
